
import { Component, Input, ViewEncapsulation } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { EnpsControl } from '@common/controls/enps-control/enps-control';
import { Address } from '@common/data-models/complex-types/address';

@Component({
    selector: 'address-form',
    templateUrl: './address-form.html',
    styleUrls: ['./address-form.scss'],
    // changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-address-form'

    }
})
export class AddressForm extends EnpsControl<Address> {

    public autocomplete: { service: google.maps.places.AutocompleteService, places: google.maps.places.PlacesService };
    public predictions: Array<any> = [];
    public locationShareUrl: string = '';
    private useAppleMaps = (localStorage.getItem('useAppleMaps')) ? localStorage.getItem('useAppleMaps') : 'FALSE';
    private sessionToken: google.maps.places.AutocompleteSessionToken;
    internalFormControl: FormControl<string | number> = new FormControl('');

    internalAddress: string = "";
    internalLocation: string = "";
    internalCity: string = "";
    internalState: string = "";
    internalCountry: string = "";

    public _width: string = "";
    public get width(): string {
        return this._width;
    }
    @Input() public set width(value: string | number) {
        if (value) {
            this._width = (typeof value === "string") ? value : (<number>value + "px");
            const matFormField = this.elmRef.nativeElement.querySelector("mat-form-field");
            if (matFormField) {
                matFormField.style.width = this._width;
            }
        }
    }

    public get value(): Address {
        return this._value;
    }

    protected _value: Address = new Address();
    public set value(value: Address) {
        if (value) {
            this._value = value;
            this._syncValueIfNecessary();
        }
    }

    public isPanelOpen: boolean = false;



    protected addressChanged() {
        this.locationShareUrl = this.createMapLink();
        this.changed.emit(this.value);
    }

    private createMapLink(): string {
        let _res = "";
        if (this.value?.formattedAddress) {
            if (this.useAppleMaps.toUpperCase() == 'TRUE') {
                _res = "http://maps.apple.com/maps?address=" + this.value.lat + "," + this.value.long;
            } else {
                if (this.value.formattedAddress != "") {
                    _res = "https://maps.google.com/maps?q=" + encodeURI(this.value.formattedAddress);
                } else {
                    _res = "";
                }
            }
        } else {
            _res = "";
        }
        return _res;
    }

    protected async handlePlaceChanged() {
        this.locationShareUrl = this.createMapLink();
    }

    ngAfterViewInit() {
        this.appService.mapService.getAutocomplete().then((autocomplete) => {
            this.autocomplete = autocomplete;
            this.sessionToken = new google.maps.places.AutocompleteSessionToken();
        });
        this.locationShareUrl = this.createMapLink();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    displayWith(item: any): any {
        return item?.description || item || "";
    }

    clearInternalFields() {
        this.internalAddress = "";
        this.internalCity = "";
        this.internalCountry = "";
        this.internalLocation = "";
        this.internalState = "";
    }

    public selectPrediction(result) {
        var predicitonAddress;
        if (result.description) {
            predicitonAddress = result.description;
        }
        this.ngZone.run(() => {
            this.autocomplete.places.getDetails({ placeId: result.place_id, sessionToken: this.sessionToken, fields: ["address_component", "adr_address", "alt_id", "formatted_address", "geometry", "icon", "id", "name", "business_status", "photo", "place_id", "plus_code", "scope", "type", "url", "utc_offset_minutes", "vicinity"] }, async (place, status) => {
                this.sessionToken = new google.maps.places.AutocompleteSessionToken();
                if ((status == google.maps.places.PlacesServiceStatus.OK) && (place && place.geometry)) {
                    this.value.isManualEditMode = false;
                    this.value.clear();
                    this.clearInternalFields();
                    this.value.lat = place.geometry.location.lat();
                    this.value.long = place.geometry.location.lng();

                    let locationName = "";
                    this.value.placeId = place.place_id;
                    if (place.types.includes('point_of_interest')) {
                        locationName = place.name;
                    }
                    this.value.title = predicitonAddress;
                    this.value.formattedAddress = place.formatted_address;
                    var streetNumber: string = "";
                    var route: string = "";
                    for (var i = 0; i < place.address_components.length; i++) {
                        var addressType = place.address_components[i];
                        if (addressType.types.includes('country')) {
                            this.value.country = place.address_components[i].long_name;
                            this.value.shortCountry = place.address_components[i].short_name;
                        }
                        if (addressType.types.includes('administrative_area_level_1')) {
                            this.value.state = place.address_components[i].long_name;
                            this.value.administrativeArea = place.address_components[i].long_name;
                            this.value.shortState = place.address_components[i].short_name;
                        }
                        if (addressType.types.includes('locality')) {
                            this.value.locality = place.address_components[i].short_name;
                        }
                        if (addressType.types.includes('sublocality')) {
                            this.value.subLocality = place.address_components[i].short_name;
                        }
                        if (addressType.types.includes('neighborhood')) {
                            this.value.neighborhood = place.address_components[i].short_name;
                        }
                        if (addressType.types.includes('premise')) {
                            this.value.locationName = place.address_components[i].short_name;
                        }
                        if (addressType.types.includes('street_number')) {
                            streetNumber = place.address_components[i].short_name;
                        }
                        if (addressType.types.includes('route')) {
                            route = place.address_components[i].short_name;
                        }
                        if (addressType.types.includes('postal_code')) {
                            this.value.postalCode = place.address_components[i].short_name;
                        }
                    }
                    this.value.city = this.value.locality ? this.value.locality : this.value.subLocality ? this.value.subLocality : this.value.neighborhood ? this.value.neighborhood : "";
                    this.value.streetAddress = ((streetNumber) ? streetNumber + " " + route : route);
                    if (locationName != this.value.streetAddress) {
                        this.value.locationName = locationName;
                    } else {
                        this.value.locationName = "";
                    }
                    await this.handlePlaceChanged();
                    this.predictions = [];
                    this.predictions.push(result);
                    this.changed.emit(this.value);
                }
            });
        });
    }

    public close() {
        if (this.isValueChanged) {
            this.value.streetAddress = this.internalAddress || this.value.streetAddress;
            this.value.locationName = this.internalLocation || this.value.locationName;
            this.value.city = this.internalCity || this.value.city;
            this.value.state = this.internalState || this.value.state;
            this.value.country = this.internalCountry || this.value.country;
            this.value.updateFormattedAddress();
            this.changed.emit(this.value);
            this.isValueChanged = false;
        }
        this.isPanelOpen = false;
    }

    public loadPredictions(event) {
        if (this.autocomplete && this.autocomplete.service && this.autocomplete.places && event.target.value) {
            this.predictions = [];
            let address = this.value.getAddressForPlacePredictions();
            if (address == "") return;
            this.autocomplete.service.getPlacePredictions({ input: address, sessionToken: this.sessionToken }, (predictions, status) => {
                if (status == google.maps.places.PlacesServiceStatus.OK) {
                    if (predictions) {
                        for (let prediction of predictions) {
                            this.predictions.push(prediction);
                        }
                    }
                }
            });
        }
    }

    public searchForPlaces(event) {
        if (this.autocomplete && this.autocomplete.service && this.autocomplete.places && event.target.value) {
            this.autocomplete.service.getPlacePredictions({ input: event.target.value, sessionToken: this.sessionToken }, (predictions, status) => {
                if (status == google.maps.places.PlacesServiceStatus.OK) {
                    this.predictions = predictions;
                    for (let result of this.predictions) {
                        result.structured_formatting.main_strings = [];
                        let lastOffset = 0;
                        for (let matchedStr of result.structured_formatting.main_text_matched_substrings) {
                            if (matchedStr.offset > lastOffset) {
                                result.structured_formatting.main_strings.push({
                                    text: result.structured_formatting.main_text.substr(lastOffset, (matchedStr.offset - lastOffset)),
                                    match: false
                                });
                                lastOffset = matchedStr.offset;
                            } else {
                                result.structured_formatting.main_strings.push({
                                    text: result.structured_formatting.main_text.substr(matchedStr.offset, matchedStr.length),
                                    match: true
                                });
                                lastOffset = matchedStr.offset + matchedStr.length;
                            }
                        }
                        if ((lastOffset > 0) && (lastOffset < result.structured_formatting.main_text.length)) {
                            result.structured_formatting.main_strings.push({
                                text: result.structured_formatting.main_text.substr(lastOffset, (result.structured_formatting.main_text.length - lastOffset)),
                                match: false
                            });
                        } else if (lastOffset == 0) {
                            result.structured_formatting.main_strings.push({
                                text: result.structured_formatting.main_text,
                                match: false
                            });
                        }
                    }
                }
            });
        } else if (!event.target.value || event.target.value.length == 0) {
            this.value.clear();
            this.predictions = [];
            this.changed.emit(this.value);
        }
    }

    public onFocus(event) {
        if (event.target.value) {
            this.loadPredictions(event);
        }
    }

    internalAddressChanged(event) {
        this.isValueChanged = true;
        this.internalAddress = event;
    }

    internalLocationChanged(event) {
        this.isValueChanged = true;
        this.internalLocation = event;
    }

    internalCityChanged(event) {
        this.isValueChanged = true;
        this.internalCity = event;
    }

    internalStateChanged(event) {
        this.isValueChanged = true;
        this.internalState = event;
    }

    internalCountryChanged(event) {
        this.isValueChanged = true;
        this.internalCountry = event;
    }

    _addValidatorIfNecessary() {
        this.internalFormControl.addValidators(Validators.required);
        this.internalFormControl.markAllAsTouched();
    }

    _syncValueIfNecessary() {
        if(this?.value?.isManualEditMode) {
            this.internalFormControl.setValue(this.value?.formattedAddress);
        } else {
            this.internalFormControl.setValue(this.value?.title);
        }
    }

    _setDisabledState(isDisabled: boolean) {
        if (isDisabled) {
            this.internalFormControl.disable();
        } else {
            this.internalFormControl.enable();
        }
    }
}
